export const DEFAULT_DEBOUNCE_MS = 300;
export const CALLS_SEARCH_DEBOUNCE_MS = 1000;
export const RESIZE_DEBOUNCE_MS = 100;

export type DebouncedFunction<Args extends unknown[]> = {
    (...args: Args): void;
    invoke: (...args: Args) => void;
    cancel: () => void;
    flush: () => void;
};

/**
 * Creates a debounced function with optional immediate invoke, cancel, and flush helpers.
 */
export const debounce = <Args extends unknown[]>(
    fn: (...args: Args) => void,
    delayMs: number
): DebouncedFunction<Args> => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let pendingArgs: Args | undefined;

    const cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = undefined;
        }

        pendingArgs = undefined;
    };

    const invoke = (...args: Args) => {
        cancel();
        fn(...args);
    };

    const flush = () => {
        if (!timeout || !pendingArgs) {
            return;
        }

        const args = pendingArgs;
        cancel();
        fn(...args);
    };

    const schedule = (...args: Args) => {
        pendingArgs = args;

        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            timeout = undefined;

            if (pendingArgs) {
                fn(...pendingArgs);
            }

            pendingArgs = undefined;
        }, delayMs);
    };

    return Object.assign(schedule, { invoke, cancel, flush });
};
